import { Image, Platform } from 'react-native';

/**
 * Enhanced ResponsiveImage component with full srcset support
 * Supports multiple image sizes and density variants for optimal loading
 *
 * @param {object} props
 * @param {object} props.sources - Image sources with size variants
 *   Format: { small: 'small.webp, small@2x.webp 2x', medium: '...', large: '...' }
 *   OR legacy format: { mobile: require('...'), desktop: require('...') }
 * @param {string} props.sizes - Sizes attribute for responsive loading
 *   Example: "(max-width: 767px) 382px, (max-width: 1200px) 669px, 791px"
 * @param {object} props.widths - Optional custom width descriptors for srcset
 *   Format: { small: 382, small2x: 764, medium: 669, medium2x: 1338, large: 791, large2x: 1582 }
 * @param {object} props.style - Style object
 * @param {string} props.accessibilityLabel - Accessibility label
 * @param {string} props.resizeMode - Resize mode (cover, contain, etc.)
 * @param {string} props.fetchpriority - Fetch priority (high, low, auto)
 * @param {object} props...rest - Other Image props
 */
const ResponsiveImage = ({
  sources,
  sizes,
  widths,
  style,
  accessibilityLabel,
  resizeMode = 'contain',
  fetchpriority,
  ...rest
}) => {
  // Helper to get URI from Metro asset
  const getAssetUri = (asset) => {
    if (!asset) return null;
    if (typeof asset === 'string') return asset;
    if (typeof asset === 'object' && asset.uri) return asset.uri;
    return String(asset);
  };

  // Check if using new multi-size format or legacy mobile/desktop format
  const isMultiSize = sources && (sources.small || sources.medium || sources.large);

  if (isMultiSize) {
    // New format: multiple size variants with density support from Metro require()
    // Build srcset from Metro assets with width descriptors for proper responsive behavior
    const srcSetParts = [];

    // Use custom widths if provided, otherwise use default hero image widths
    const defaultWidths = {
      small: 360,
      small2x: 720,
      medium: 537,
      medium2x: 1074,
      large: 728,
      large2x: 1456,
    };
    const actualWidths = widths || defaultWidths;

    const sizeConfig = [
      { base: 'small', retina: 'small2x', baseWidth: actualWidths.small, retinaWidth: actualWidths.small2x },
      { base: 'medium', retina: 'medium2x', baseWidth: actualWidths.medium, retinaWidth: actualWidths.medium2x },
      { base: 'large', retina: 'large2x', baseWidth: actualWidths.large, retinaWidth: actualWidths.large2x },
    ];

    for (const { base, retina, baseWidth, retinaWidth } of sizeConfig) {
      if (sources[base]) {
        const baseUri = getAssetUri(sources[base]);
        if (baseUri) srcSetParts.push(`${baseUri} ${baseWidth}w`);

        if (sources[retina]) {
          const retinaUri = getAssetUri(sources[retina]);
          if (retinaUri) srcSetParts.push(`${retinaUri} ${retinaWidth}w`);
        }
      }
    }

    const srcSet = srcSetParts.join(', ');

    // Use the largest size as the default source for better quality
    const defaultSource = sources.large || sources.medium || sources.small;
    const defaultSrc = getAssetUri(defaultSource);

    // On web, use native img element for proper srcset support
    if (Platform.OS === 'web') {
      // Flatten the style array/object and convert RN styles to web styles
      const flatStyle = Array.isArray(style) ? Object.assign({}, ...style) : style || {};
      const imgStyle = {
        ...flatStyle,
        objectFit: resizeMode || 'contain',
        display: 'block',
      };

      return (
        <img
          src={defaultSrc}
          srcSet={srcSet}
          sizes={sizes || '100vw'}
          alt={accessibilityLabel || ''}
          style={imgStyle}
          fetchPriority={fetchpriority}
          {...rest}
        />
      );
    }

    // On native, use React Native Image component
    return (
      <Image
        source={{ uri: defaultSrc }}
        style={style}
        resizeMode={resizeMode}
        accessibilityLabel={accessibilityLabel}
        {...rest}
      />
    );
  }

  // Legacy format: mobile/desktop only
  const getUri = (source) => {
    if (typeof source === 'object' && source.uri) {
      return source.uri;
    }
    return source;
  };

  const mobileUri = getUri(sources.mobile);
  const desktopUri = getUri(sources.desktop);

  return (
    <Image
      source={sources.mobile}
      srcSet={`${mobileUri} 767w, ${desktopUri} 768w`}
      sizes={sizes || '(max-width: 767px) 100vw, 768px'}
      style={style}
      resizeMode={resizeMode}
      accessibilityLabel={accessibilityLabel}
      // @ts-ignore - fetchpriority is not in React Native types but works on web
      fetchpriority={fetchpriority}
      {...rest}
    />
  );
};

export default ResponsiveImage;
